<template>
  <div 
    class="login-container"
    :class="{ 'opacity-0': !backgroundLoaded }"
    :style="backgroundStyle"
  >
    <div class="login-form-container">
      <div class="max-w-md w-full space-y-6 sm:space-y-8 bg-white/95 dark:bg-gray-800/90 backdrop-blur-sm p-4 sm:p-6 md:p-8 rounded-lg shadow-lg transform transition-all duration-300 hover:shadow-2xl">
        <div>
          <h2 class="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-extrabold text-gray-800 dark:text-white">
            K&C Portfolio Manager
          </h2>
          <p class="mt-2 text-center text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Réinitialisation du mot de passe
          </p>
        </div>
        <form class="mt-6 sm:mt-8 space-y-4 sm:space-y-6" @submit.prevent="handleSubmit">
          <div>
            <label for="email" class="sr-only">Email</label>
            <input
              id="email"
              v-model="email"
              name="email"
              type="email"
              required
              class="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 bg-white dark:bg-gray-700 placeholder-gray-500 text-gray-900 dark:text-white dark:border-gray-600 dark:placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
              placeholder="Adresse email"
            />
          </div>

          <div v-if="message" class="bg-green-50 dark:bg-green-900/50 border border-green-400 text-green-700 dark:text-green-300 px-4 py-3 rounded relative" role="alert">
            <span class="block sm:inline">{{ message }}</span>
          </div>

          <div v-if="error" class="bg-red-50 dark:bg-red-900/50 border border-red-400 text-red-700 dark:text-red-300 px-4 py-3 rounded relative" role="alert">
            <span class="block sm:inline">{{ error }}</span>
          </div>

          <div class="mt-2">
            <CaptchaWidget v-model="captchaToken" />
          </div>

          <div>
            <button
              type="submit"
              :disabled="loading"
              class="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              <span v-if="loading" class="absolute left-0 inset-y-0 flex items-center pl-3">
                <svg class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </span>
              {{ loading ? 'Envoi en cours...' : 'Envoyer le lien' }}
            </button>
          </div>

          <div class="mt-4 space-y-2">
            <router-link
              to="/login"
              class="btn-outline-primary w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12H9m0 0l3-3m-3 3l3 3M4 6h16v12H4z" />
              </svg>
              <span>Retour à la connexion</span>
            </router-link>

            <router-link
              to="/register"
              class="btn-ghost-indigo w-full"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.121 17.804A9 9 0 1112 21v-2m0 0v-2m0 2h-2m2 0h2" />
              </svg>
              <span>Créer un compte</span>
            </router-link>
          </div>
        </form>
      </div>
    </div>
    <AuthFooter @open-legal="isLegalModalOpen = true" />
    <LegalModal :is-open="isLegalModalOpen" @close="isLegalModalOpen = false" />
  </div>
</template>

<script>
import { defineComponent, ref, onMounted } from 'vue'
import axiosInstance from '@/config/axios'
import CaptchaWidget from '@/components/common/CaptchaWidget.vue'
import AuthFooter from '@/components/auth/AuthFooter.vue'
import LegalModal from '@/components/auth/LegalModal.vue'

export default defineComponent({
  name: 'ForgotPassword',
  components: { CaptchaWidget, AuthFooter, LegalModal },
  setup() {
    const email = ref('')
    const loading = ref(false)
    const error = ref('')
    const message = ref('')
    const captchaToken = ref('')
    const isLegalModalOpen = ref(false)
    const backgroundLoaded = ref(false)
    const backgroundStyle = ref({
      backgroundImage: '',
      backgroundColor: '#f3f4f6'
    })

    onMounted(async () => {
      try {
        const { default: bgImage } = await import('@/assets/backgroundImage.js')
        backgroundStyle.value.backgroundImage = `url(${bgImage})`
        backgroundLoaded.value = true
      } catch (err) {
        console.error('Erreur lors du chargement de l\'image:', err)
        backgroundLoaded.value = true
      }
    })

    const handleSubmit = async () => {
      try {
        loading.value = true
        error.value = ''
        message.value = ''

        await axiosInstance.post('/auth/request-password-reset/', {
          email: email.value,
          captchaToken: captchaToken.value,
        })

        message.value = 'Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.'
        email.value = ''
      } catch (err) {
        error.value = err.response?.data?.error || 'Une erreur est survenue'
      } finally {
        loading.value = false
      }
    }

    return {
      email,
      loading,
      error,
      message,
      captchaToken,
      handleSubmit,
      backgroundStyle,
      backgroundLoaded,
      isLegalModalOpen
    }
  }
})
</script>

<style>
.login-container {
  height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transition: opacity 0.5s ease-in-out;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
}

.login-form-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  flex: 1;
  padding: 0.75rem;
}

/* Styles responsive */
@media (min-width: 640px) {
  .login-form-container {
    padding: 1.5rem;
  }
}

/* Animation de transition */
.transform {
  transition-property: transform, box-shadow;
}

.hover\:shadow-2xl:hover {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
}
</style>
